/* @moldura padrao */
/**
 * page-completo.tsx (autorizações) — a lista no escritório. Rota `/autorizacoes`.
 *
 * Este é o arranjo de tablet, notebook e PC: tabela com todas as colunas,
 * filtro por status E por tipo, e o botão de criar. É o conteúdo que morava em
 * `page.tsx` até 25/08 — só mudou de nome, não de comportamento.
 *
 * O irmão de celular é `page-celular.tsx`, e quem escolhe entre os dois é
 * `page.tsx`. Convenção em `11-convencao-visual.md` §8.
 *
 * Filtros (status/tipo) + tabela; linha clicável abre a página dedicada. Criar é
 * em /autorizacoes/nova.
 *
 * ─── AS QUATRO VISTAS (04/09/2026, ficha #26) ───────────────────────────────
 * Dele: *"na lista a gente tem que estar sempre vendo o que está pendente e em
 * andamento — em andamento inclui o que foi autorizado, ou seja, o que não foi
 * fechado nem arquivado"*.
 *
 *   Em andamento   o trabalho vivo: nem arquivada, nem no lixo (o padrão)
 *   Acompanhamento uma linha por ETAPA aberta — a fila da Suany e da Patrícia
 *   Arquivadas     tiradas da frente de propósito
 *   Lixeira        "isso aqui não deveria existir" — e mesmo assim fica no banco
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { useAuth } from '@/auth/auth-context';
import type { Autorizacao, TipoOpcao } from './shared';
import { STATUS, andamentoDaAutorizacao, badge, rotuloStatus, valorResumo, varianteStatus } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import type { Etapa } from './CustomEtapas';

const TODOS = 'todos';

type Vista = 'andamento' | 'acompanhamento' | 'arquivadas' | 'lixo';

/** A etapa na fila, com o pedido a que pertence. */
interface EtapaNaFila extends Etapa {
  autorizacao: { id: number; titulo: string; status: string; cliente_nome?: string | null };
}

export function AutorizacoesCompletoPage() {
  const navigate = useNavigate();
  const { pode, usuario } = useAuth();
  const [lista, setLista] = useState<Autorizacao[]>([]);
  const [tipos, setTipos] = useState<TipoOpcao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [fStatus, setFStatus] = useState(TODOS);
  const [fTipo, setFTipo] = useState(TODOS);
  // A fila "sem pedido" (078): autorizadas de tipo que pede pedido de venda e
  // ainda sem o número — o que a gestão precisa fechar.
  const [fSemPedido, setFSemPedido] = useState(false);
  const [vista, setVista] = useState<Vista>('andamento');
  const [fila, setFila] = useState<EtapaNaFila[]>([]);
  const [soMinhas, setSoMinhas] = useState(false);

  useEffect(() => {
    api
      .get<TipoOpcao[]>('/autorizacoes/tipos')
      .then(setTipos)
      .catch(() => undefined);
  }, []);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      if (fStatus !== TODOS) params.set('status', fStatus);
      if (fTipo !== TODOS) params.set('tipo_id', fTipo);
      if (fSemPedido) params.set('sem_pedido', 'true');
      params.set('vista', vista === 'acompanhamento' ? 'andamento' : vista);
      const qs = params.toString();
      setLista(await api.get<Autorizacao[]>(`/autorizacoes${qs ? `?${qs}` : ''}`));
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar as autorizações.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fStatus, fTipo, fSemPedido, vista]);

  // A fila do acompanhamento é outra pergunta ("o que falta fazer"), então é
  // outra chamada — e só quando a aba está aberta.
  async function carregarFila() {
    try {
      setFila(await api.get<EtapaNaFila[]>(
        `/autorizacoes/acompanhamento${soMinhas ? '?minhas=true' : ''}`,
      ));
    } catch {
      setFila([]); // 403 = quem não acompanha não vê a aba
    }
  }

  useEffect(() => {
    if (vista === 'acompanhamento') carregarFila();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, soMinhas]);

  async function arquivar(a: Autorizacao, arquivada: boolean) {
    try {
      await api.put(`/autorizacoes/${a.id}/arquivamento`, { arquivada });
      toast.success(arquivada ? 'Arquivada.' : 'Tirada do arquivo.');
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para arquivar.');
    }
  }

  async function mandarParaLixo(a: Autorizacao, no_lixo: boolean) {
    try {
      await api.put(`/autorizacoes/${a.id}/lixo`, { no_lixo });
      toast.success(no_lixo ? 'Foi para a lixeira.' : 'Tirada da lixeira.');
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para fazer isso.');
    }
  }

  return (
    <CustomMolduraPagina
      titulo="Autorizações"
      descricao="Pedidos de autorização — registro e aprovação"
      acoes={
        // Mesma trava do POST /autorizacoes e do atalho do Início.
        pode('autorizacoes.pedido.abrir') ? (
            <button
              className="btn btn-theme"
              onClick={() => navigate('/autorizacoes/nova')}
            >
              <i className="bi bi-plus-lg me-1"></i> Nova autorização
            </button>
        ) : undefined
      }
    >

      {/* AS QUATRO VISTAS. "Em andamento" é o padrão e inclui as autorizadas
          que ainda têm etapa: elas não acabaram, só foram decididas. */}
      <ul className="nav nav-tabs mb-3">
        {([
          ['andamento', 'Em andamento', 'bi-inboxes'],
          ['acompanhamento', 'Acompanhamento', 'bi-list-check'],
          ['arquivadas', 'Arquivadas', 'bi-archive'],
          ['lixo', 'Lixeira', 'bi-trash3'],
        ] as [Vista, string, string][])
          .filter(([v]) => v !== 'acompanhamento' || pode('autorizacoes.etapas.ver'))
          .map(([v, rotulo, icone]) => (
            <li className="nav-item" key={v}>
              <button
                type="button"
                className={`nav-link ${vista === v ? 'active' : ''}`}
                onClick={() => setVista(v)}
              >
                <i className={`bi ${icone} me-1`}></i>
                {rotulo}
                {v === 'acompanhamento' && fila.length > 0 && (
                  <span className="badge casaa-selo-destaque ms-2">{fila.length}</span>
                )}
              </button>
            </li>
          ))}
      </ul>

      <div className="card adminuiux-card">
        <div className="card-body">
          {/* Filtros — na aba de acompanhamento a pergunta é outra, e o
              único filtro que faz sentido é "só as minhas". */}
          {vista === 'acompanhamento' ? (
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="f-so-minhas"
                  checked={soMinhas}
                  onChange={(e) => setSoMinhas(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="f-so-minhas">
                  Só as minhas
                </label>
              </div>
              <span className="small text-secondary">
                O que falta fazer, uma linha por etapa — vencidas primeiro.
              </span>
            </div>
          ) : (
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
              >
                <option value={TODOS}>Todos os status</option>
                {STATUS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fTipo}
                onChange={(e) => setFTipo(e.target.value)}
              >
                <option value={TODOS}>Todos os tipos</option>
                {tipos.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex align-items-center">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="f-sem-pedido"
                  checked={fSemPedido}
                  onChange={(e) => setFSemPedido(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="f-sem-pedido">
                  Só autorizadas sem pedido
                </label>
              </div>
            </div>
            {(fStatus !== TODOS || fTipo !== TODOS || fSemPedido) && (
              <div className="col-auto">
                <button
                  className="btn btn-sm btn-link"
                  onClick={() => {
                    setFStatus(TODOS);
                    setFTipo(TODOS);
                    setFSemPedido(false);
                  }}
                >
                  Limpar
                </button>
              </div>
            )}
          </div>
          )}

          {vista === 'acompanhamento' ? (
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr className="text-secondary text-uppercase" style={{ fontSize: '0.75rem' }}>
                    <th>O que falta fazer</th>
                    <th>De quem</th>
                    <th>Prazo</th>
                    <th>No pedido</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {fila.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => navigate(`/autorizacoes/${e.autorizacao.id}`)}
                      style={{ cursor: 'pointer' }}
                      className={e.vencida ? 'table-danger' : ''}
                    >
                      <td className="fw-medium">
                        <span
                          className={`casaa-etapa-bolinha d-inline-flex me-2 align-middle ${
                            e.na_vez ? 'casaa-bolinha-na-vez' : 'casaa-bolinha-esperando'
                          }`}
                        ></span>
                        {e.titulo}
                      </td>
                      <td className="text-secondary text-nowrap">
                        {e.responsavel_id === usuario?.id ? (
                          <strong>{e.responsavel_nome}</strong>
                        ) : (
                          e.responsavel_nome ?? '— sem dono —'
                        )}
                      </td>
                      <td className={`text-nowrap ${e.vencida ? 'text-danger fw-medium' : 'text-secondary'}`}>
                        {e.prazo ? formatarData(e.prazo) : '—'}
                        {e.adiamentos > 0 && (
                          <span className="ms-1 small" title="adiamentos">↻{e.adiamentos}</span>
                        )}
                      </td>
                      <td className="text-secondary text-truncate" style={{ maxWidth: 260 }}>
                        {e.autorizacao.titulo}
                        {e.autorizacao.cliente_nome ? ` · ${e.autorizacao.cliente_nome}` : ''}
                      </td>
                      <td className="text-end text-nowrap">
                        {!e.na_vez && <span className="badge casaa-selo-neutro">esperando</span>}
                      </td>
                    </tr>
                  ))}
                  {fila.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-5 text-center text-secondary">
                        Nada em aberto. Quando uma autorização deixar combinado
                        para depois, a etapa aparece aqui.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table
                className="table align-middle table-hover mb-0"
                style={{ fontSize: '0.9rem' }}
              >
                <thead>
                  <tr
                    className="text-secondary text-uppercase"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <th>Título</th>
                    <th>Tipo</th>
                    <th>Loja</th>
                    <th className="text-end">Valor / %</th>
                    <th>Solicitante</th>
                    <th>Cliente · pedido</th>
                    <th className="text-nowrap">Status</th>
                    <th className="text-center">Msgs</th>
                    <th className="text-nowrap">Aberta em</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => navigate(`/autorizacoes/${a.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td
                        className="fw-medium text-truncate"
                        style={{ maxWidth: 260 }}
                        title={a.titulo}
                      >
                        {a.titulo}
                      </td>
                      <td className="text-secondary">{a.tipo_nome ?? '—'}</td>
                      <td className="text-secondary text-nowrap">{a.loja_nome ?? '—'}</td>
                      <td className="text-nowrap text-end">{valorResumo(a)}</td>
                      <td className="text-secondary">
                        {a.solicitante_nome ?? a.registrante_nome ?? '—'}
                      </td>
                      {/* Sobre o quê: o cliente (e o pedido, quando já tem
                          número) ou a OS. A autorizada sem pedido avisa. */}
                      <td className="text-secondary text-nowrap">
                        {a.cliente_nome ? (
                          <>
                            {a.cliente_nome}
                            {a.numero_pedido_venda ? (
                              <span className="ms-1">· PV {a.numero_pedido_venda}</span>
                            ) : a.sem_pedido ? (
                              <span className="ms-1 text-warning" title="Autorizada, ainda sem o número do pedido de venda">
                                · sem pedido
                              </span>
                            ) : null}
                          </>
                        ) : (
                          a.entidade_label ?? '—'
                        )}
                      </td>
                      <td className="text-nowrap">
                        <span className={`badge ${badge(varianteStatus(a.status))}`}>
                          {rotuloStatus(a.status)}
                        </span>
                        {/* O ANDAMENTO ao lado (092). Sem ele a autorizada que
                            já encerrou ficaria igual à que ainda espera o
                            pedido — e é essa diferença que a lista existe para
                            mostrar. */}
                        {andamentoDaAutorizacao(a) && (
                          <span className={`badge ms-1 ${andamentoDaAutorizacao(a)!.cor}`}>
                            {andamentoDaAutorizacao(a)!.rotulo}
                          </span>
                        )}
                      </td>
                      <td className="text-center text-secondary">
                        {a.qtd_mensagens > 0 ? (
                          <span>
                            <i className="bi bi-chat-dots me-1"></i>
                            {a.qtd_mensagens}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-nowrap text-secondary">
                        {a.criado_em ? formatarData(a.criado_em) : '—'}
                      </td>
                      {/* Tirar da frente. `stopPropagation` porque a linha
                          inteira navega — sem isso, arquivar abriria o pedido. */}
                      <td className="text-end text-nowrap" onClick={(ev) => ev.stopPropagation()}>
                        {/* A lixeira pede o que a rota pede: ser quem registrou,
                            ou poder cancelar o pedido dos outros. */}
                        {!(a.registrante_id === usuario?.id || pode('autorizacoes.pedido.cancelar')) ? (
                          <button
                            className="btn btn-sm btn-link text-secondary text-decoration-none"
                            onClick={() => arquivar(a, !a.arquivada)}
                            title={a.arquivada ? 'Tirar do arquivo' : 'Arquivar — some desta lista'}
                          >
                            <i className={`bi ${a.arquivada ? 'bi-archive-fill' : 'bi-archive'}`}></i>
                          </button>
                        ) : vista === 'lixo' ? (
                          <button
                            className="btn btn-sm btn-link text-decoration-none"
                            onClick={() => mandarParaLixo(a, false)}
                            title="Tirar da lixeira"
                          >
                            <i className="bi bi-arrow-counterclockwise"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm btn-link text-secondary text-decoration-none"
                              onClick={() => arquivar(a, !a.arquivada)}
                              title={a.arquivada ? 'Tirar do arquivo' : 'Arquivar — some desta lista'}
                            >
                              <i className={`bi ${a.arquivada ? 'bi-archive-fill' : 'bi-archive'}`}></i>
                            </button>
                            <button
                              className="btn btn-sm btn-link text-secondary text-decoration-none"
                              onClick={() => mandarParaLixo(a, true)}
                              title="Jogar no lixo — continua no banco"
                            >
                              <i className="bi bi-trash3"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {lista.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-5 text-center text-secondary">
                        {vista === 'lixo'
                          ? 'A lixeira está vazia.'
                          : vista === 'arquivadas'
                            ? 'Nada arquivado ainda.'
                            : 'Nenhuma autorização encontrada.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
